// ==========================
// Version 7 — MapPicker.js (mapId wiring)
// - Uses withBrandMapOptions() so Map ID is applied when present
// ==========================
import React, { useState, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { MAPS_LOADER_OPTIONS, withBrandMapOptions } from './mapsConfig';

const NAVY = '#0A1A33';
const GOLD = '#C9A44C';
const IVORY = '#FDFCF9';
const WARM_GRAY = '#888888';

const containerStyle = {
  width: '100%',
  height: 300,
  borderRadius: 12,
  marginTop: 10,
  border: `1px solid rgba(201,164,76,0.35)`,
};

const defaultCenter = { lat: 51.5074, lng: -0.1278 }; // London default

function MapPicker({ onLocationSelect }) {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapVisible, setMapVisible] = useState(false);
  const [locationLabel, setLocationLabel] = useState('');

  const { isLoaded } = useJsApiLoader(MAPS_LOADER_OPTIONS);

  const fetchLocationLabel = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${MAPS_LOADER_OPTIONS.googleMapsApiKey}`
      );
      const data = await response.json();
      if (data.results && data.results.length > 0) {
        const components = data.results[0].address_components;
        const getComponent = (types) =>
          components.find((c) => types.some((t) => c.types.includes(t)))?.long_name;

        const sublocality = getComponent(['sublocality', 'neighborhood']);
        const city = getComponent(['locality', 'postal_town']);
        const region = getComponent(['administrative_area_level_1']);
        const country = getComponent(['country']);

        const parts = [sublocality, city, region, country].filter(Boolean);
        setLocationLabel(parts.join(', ') || 'Location found');
      } else {
        setLocationLabel('Location found');
      }
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      setLocationLabel('Location found');
    }
  };

  const handleMapClick = useCallback(
    (e) => {
      const coords = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      setSelectedLocation(coords);
      setLocationLabel('');
      fetchLocationLabel(coords.lat, coords.lng);
      onLocationSelect(coords);
    },
    [onLocationSelect]
  );

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setSelectedLocation(coords);
          fetchLocationLabel(coords.lat, coords.lng);
          onLocationSelect(coords);
        },
        (error) => {
          alert('Failed to get your location. Please allow location access.');
          console.error(error);
        }
      );
    } else {
      alert('Geolocation is not supported by your browser.');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
        <button type="button" onClick={() => setMapVisible(!mapVisible)} style={styles.btnPrimary}>
          {mapVisible ? 'Hide Map' : 'Pick from Map'}
        </button>
        <button type="button" onClick={handleUseCurrentLocation} style={styles.btnGhost}>
          Use My Location
        </button>
      </div>

      {selectedLocation && (
        <div style={styles.selected}>
          ✅ Selected: {locationLabel || `${selectedLocation.lat.toFixed(4)}, ${selectedLocation.lng.toFixed(4)}`}
        </div>
      )}

      {mapVisible && isLoaded && (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={selectedLocation || defaultCenter}
          zoom={13}
          onClick={handleMapClick}
          options={withBrandMapOptions()}
        >
          {selectedLocation && <Marker position={selectedLocation} title="Selected location" />}
        </GoogleMap>
      )}
    </div>
  );
}

const styles = {
  btnPrimary: {
    backgroundColor: GOLD,
    color: NAVY,
    padding: '10px 14px',
    border: 'none',
    borderRadius: 12,
    cursor: 'pointer',
    fontWeight: 800,
  },
  btnGhost: {
    backgroundColor: 'transparent',
    color: GOLD,
    padding: '10px 14px',
    border: `1px solid ${GOLD}`,
    borderRadius: 12,
    cursor: 'pointer',
    fontWeight: 800,
  },
  selected: { marginBottom: 10, color: WARM_GRAY, fontStyle: 'italic' },
};

export default React.memo(MapPicker);
// ==========================
// End Version 7 — MapPicker.js
// ==========================
